import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core'
import { Sale } from 'app/interfaces/sale.interface'
import { environment } from 'environments/environment'
import { debounceTime, map, Observable, of } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { UserInfo } from './api.service'

@Injectable({
    providedIn: 'root'
})
export class SalesService {
    private _sales: WritableSignal<Sale[]> = signal<Sale[]>([])
    public readonly sales: Signal<Sale[]> = this._sales.asReadonly()
    public static readonly ACTIOS_CUT_OFF_DAYS = 180

    private _hasNextPage: WritableSignal<boolean> = signal<boolean>(false)
    public readonly hasNextPage: Signal<boolean> = this._hasNextPage.asReadonly()

    private _httpClient!: HttpClient

    constructor(private translate: TranslateService) {
        this._httpClient = inject(HttpClient)
        this.translate.get([
            'my_sales.cta_approved_expired'
        ])
    }

    public loadSalesList(status: string | null = null, page: number = 0, size: number = 10, sort: string = 'id,desc', search: string | null = null): Observable<Sale[]> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString()).set('sort', sort)

        if (status && status !== 'all') {
            params = params.set('status', status.toUpperCase() ?? '')
        }
        if (search) {
            params = params.set('search', search ?? '')
        }

        return this._httpClient.get(`${environment.API_URL}/sales`, { params }).pipe(
            map((response: any) => {
                if (page === 0) {
                    this._sales.set(response.sales as Sale[])
                } else {
                    this._sales.update((sales: Sale[]) => [...sales, ...(response.sales as Sale[])])
                }
                this._hasNextPage.set(response.hasNextPage ?? false)
                return response.sales as Sale[]
            })
        )
    }

    public downloadWarranty(saleId: string, warrantyId: string) {
        return this._httpClient.get(`${environment.API_URL}/sales/${saleId}/warranty/${warrantyId}`, {
            responseType: 'blob' as 'blob'
        })
    }

    public updateCustomerName(saleId: string, customerName: string): Observable<void> {
        const body = customerName
        return this._httpClient.post<void>(`${environment.API_URL}/sales/${saleId}/customer-name`, body).pipe(
            debounceTime(500)
        )
    }

    public removeSale(saleId: string): Observable<void> {
        return of()
    }

    public completeSale(saleId: string): Observable<void> {
        return of()
    }

    public activateWarranty(saleId: string): Observable<void> {
        return of()
    }

    public sendInstructions(saleId: string): Observable<void> {
        return of()
    }

    public reviewSale(saleId: string): Observable<void> {
        return of()
    }

    public getSaleActions(sale: Sale, userInfo: UserInfo) {
        const isInvoiceExpired = sale.invoiceDate !== null && new Date(sale.invoiceDate).getTime() < new Date().getTime() - SalesService.ACTIOS_CUT_OFF_DAYS * 24 * 60 * 60 * 1000
        const salesStatus: any = {
            DENIED: {
                primaryButton: {
                    actionMessage: 'my_sales.edit',
                    actionOperation: 'edit'
                },
                ctaText: 'my_sales.cta_approved',
                title: 'my_sales.denied',
                subtitle: null,
                description: null,
                waitActionMessage: null,
                iconBadgeClass: 'sale denied',
                textBadgeClass: 'sale denied',
                scoreBoxClass: 'gray',
                pointsText: 'my_sales.denied_points'
            },
            REQUESTED: {
                primaryButton: {
                    actionMessage: 'my_sales.activate_warranty',
                    actionOperation: 'activateWarranty'
                },
                secondaryButton: {
                    actionMessage: 'my_sales.send_instructions',
                    actionOperation: 'sendInstructions'
                },
                ctaText: 'my_sales.cta_approved',
                title: 'my_sales.requested',
                subtitle: userInfo.accountType !== 'FABRICATOR' ? 'my_sales.requested_subtitle' : null,
                description: null,
                waitActionMessage: null,
                iconBadgeClass: 'sale requested',
                textBadgeClass: 'sale requested',
                scoreBoxClass: 'gray',
                pointsText: 'my_sales.points_to_validate'
            },
            PENDING_APPROVAL: {
                primaryButton: {
                    actionMessage: 'my_sales.activate_warranty',
                    actionOperation: 'activateWarranty'
                },
                secondaryButton: {
                    actionMessage: 'my_sales.send_instructions',
                    actionOperation: 'sendInstructions'
                },
                ctaText: 'my_sales.cta_approved',
                title: 'my_sales.requested',
                subtitle: userInfo.accountType !== 'FABRICATOR' ? 'my_sales.requested_subtitle' : null,
                description: null,
                waitActionMessage: null,
                iconBadgeClass: 'sale requested',
                textBadgeClass: 'sale requested',
                scoreBoxClass: 'gray',
                pointsText: 'my_sales.points_to_validate'
            },
            DRAFT: {
                primaryButton: {
                    actionMessage: 'my_sales.complete1',
                    actionOperation: 'completeSale'
                },
                secondaryButton: {
                    actionMessage: 'my_sales.remove',
                    actionOperation: 'removeSale'
                },
                ctaText: 'my_sales.cta_draft',
                title: 'my_sales.draft',
                subtitle: 'my_sales.draft_subtitle',
                description: null,
                waitActionMessage: null,
                iconBadgeClass: 'sale draft',
                textBadgeClass: 'sale draft',
                scoreBoxClass: 'gray',
                pointsText: 'my_sales.points_to_validate'
            },
            APPROVED: {
                primaryButton: {
                    actionMessage: 'my_sales.activate_warranty',
                    actionOperation: 'activateWarranty'
                },
                secondaryButton: {
                    actionMessage: 'my_sales.send_instructions',
                    actionOperation: 'sendInstructions'
                },
                ctaText: 'my_sales.cta_approved',
                title: 'my_sales.approved',
                subtitle: isInvoiceExpired ? 'my_sales.approved_subtitle' : null,
                description: null,
                waitActionMessage: null,
                iconBadgeClass: 'sale approved',
                textBadgeClass: 'sale approved',
                scoreBoxClass: 'gray',
                pointsText: 'my_sales.generated_points'
            },
            VALID: {
                primaryButton: {
                    actionMessage: 'my_sales.activate_warranty',
                    actionOperation: 'activateWarranty'
                },
                secondaryButton: {
                    actionMessage: 'my_sales.send_instructions',
                    actionOperation: 'sendInstructions'
                },
                ctaText: 'my_sales.cta_approved',
                title: 'my_sales.valid',
                subtitle: isInvoiceExpired ? 'my_sales.approved_subtitle' : null,
                description: null,
                waitActionMessage: null,
                iconBadgeClass: 'sale approved',
                textBadgeClass: 'sale approved',
                scoreBoxClass: 'gray',
                pointsText: 'my_sales.generated_points'
            }
        }

        if (sale.status !== 'DRAFT' && sale.warrantyNumber !== null) {
            const status = { ...salesStatus[sale.status] }
            status.primaryButton = {
                actionMessage: 'my_sales.download_warranty',
                actionOperation: 'downloadWarranty'
            }
            status.secondaryButton = null
            status.ctaText = 'my_sales.sale_warranty_activated_cta_text'


            if (sale.status === 'DENIED') {
                status.ctaText = 'my_sales.cta_denied'
                status.primaryButton = {
                    actionMessage: 'my_sales.edit',
                    actionOperation: 'completeSale'
                }
            }

            return status
        } else if (sale.status !== 'DRAFT' && sale.invoiceDate !== null && new Date(sale.invoiceDate).getTime() < new Date().getTime() - SalesService.ACTIOS_CUT_OFF_DAYS * 24 * 60 * 60 * 1000) {
            const status = { ...salesStatus[sale.status] }
            status.primaryButton = undefined
            status.secondaryButton = undefined

            if (sale.status === 'DENIED') {
                status.ctaText = 'my_sales.cta_denied'
                status.primaryButton = {
                    actionMessage: 'my_sales.edit',
                    actionOperation: 'completeSale'
                }
            }
            if (sale.status === 'REQUESTED') status.ctaText = 'my_sales.cta_requested'
            if (sale.status === 'PENDING_APPROVAL') status.ctaText = 'my_sales.cta_requested'
            if (sale.status === 'APPROVED') status.ctaText = 'my_sales.cta_approved_expired'
            if (sale.status === 'VALID') status.ctaText = 'my_sales.cta_approved_expired'

            return status
        }

        const status = salesStatus[sale?.status] ?? {}

        if (sale.status === 'DENIED') {
            status.ctaText = 'my_sales.cta_denied'
            status.primaryButton = {
                actionMessage: 'my_sales.edit',
                actionOperation: 'completeSale'
            }
        }

        return status
    }

    private loadTranslations() {
        this.translate.get([
            'my_sales.draft',
            'my_sales.approved',
            'my_sales.denied',
            'my_sales.requested',
            'my_sales.valid',
            'my_sales.edit',
            'my_sales.cta_approved',
            'my_sales.denied_points',
            'my_sales.activate_warranty',
            'my_sales.send_instructions',
            'my_sales.requested_subtitle',
            'my_sales.points_to_validate',
            'my_sales.complete1',
            'my_sales.remove',
            'my_sales.cta_draft',
            'my_sales.draft_subtitle',
            'my_sales.approved_subtitle',
            'my_sales.generated_points',
            'my_sales.download_warranty',
            'my_sales.sale_warranty_activated_cta_text',
            'my_sales.cta_denied',
            'my_sales.cta_requested',
            'my_sales.cta_approved_expired'
        ])
    }
}
